import React, { useEffect, useState, useCallback, useRef } from "react";
import { createRoot } from "react-dom/client";
import { AlertCircle, X } from "lucide-react";
import { api, demo, supabase } from "./data";
import { Storefront } from "./Storefront";
import { Admin } from "./admin";
import { Auth } from "./auth";
import { Tracking } from "./Tracking";
import { resolveRoute } from "./routing";
import "./styles.css";
function App() {
  const [route, setRoute] = useState(() => resolveRoute(location).route),
    [user, setUser] = useState(null),
    [data, setData] = useState(null),
    [error, setError] = useState(""),
    [toast, setToast] = useState(null),
    [loading, setLoading] = useState(true);
  const notify = useCallback((text, type = "success") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 5500);
  }, []);
  const admin = route.startsWith("#admin");
  const knownOrders = useRef(null);
  useEffect(() => {
    if (!admin || !user || !data?.orders) { knownOrders.current = null; return; }
    const incoming = data.orders.filter(o => o.status === "Pendiente" && !knownOrders.current?.has(o.id));
    if (knownOrders.current && incoming.length) notify(`${incoming.length} pedido${incoming.length > 1 ? "s" : ""} nuevo${incoming.length > 1 ? "s" : ""}. Revisa la campana o Pedidos pendientes.`);
    knownOrders.current = new Set(data.orders.map(o => o.id));
  }, [admin, user, data?.orders, notify]);
  const refresh = useCallback(async () => {
    try {
      const value = await api.load(admin && !!user);
      setData(value);
      setError("");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [admin, !!user]);
  useEffect(() => {
    const change = () => {
      const next = resolveRoute(location);
      if (
        next.canonicalUrl &&
        next.canonicalUrl !==
          `${location.pathname}${location.search}${location.hash}`
      )
        history.replaceState(null, "", next.canonicalUrl);
      setRoute(next.route);
    };
    change();
    window.addEventListener("hashchange", change);
    window.addEventListener("popstate", change);
    return () => {
      window.removeEventListener("hashchange", change);
      window.removeEventListener("popstate", change);
    };
  }, []);
  useEffect(() => {
    if (demo) {
      if (sessionStorage.getItem("dilo-demo-admin"))
        setUser({ id: "demo-owner", email: "demo@dilo.local" });
      return;
    }
    supabase.auth
      .getSession()
      .then(({ data }) => setUser(data.session?.user || null));
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null);
      if (event === "PASSWORD_RECOVERY") location.hash = "#reset-password";
    });
    return () => sub.subscription.unsubscribe();
  }, []);
  useEffect(() => {
    refresh();
  }, [refresh]);
  useEffect(() => {
    const fn = () => refresh();
    window.addEventListener("storage", fn);
    window.addEventListener("focus", fn);
    window.addEventListener("dilo-change", fn);
    const timer = setInterval(() => {
      if (!document.hidden) refresh();
    }, admin && user ? 7000 : 30000);
    return () => {
      window.removeEventListener("storage", fn);
      window.removeEventListener("focus", fn);
      window.removeEventListener("dilo-change", fn);
      clearInterval(timer);
    };
  }, [refresh]);
  async function run(fn, message = "Cambios guardados") {
    try {
      const result = await fn();
      await refresh();
      if (message) notify(message);
      return result;
    } catch (e) {
      notify(e.message, "error");
      throw e;
    }
  }
  const props = { data, user, route, run, refresh, notify, onUser: setUser };
  let content;
  if (
    route.startsWith("#login") ||
    route.startsWith("#reset-password") ||
    (admin && !user)
  )
    content = <Auth {...props} />;
  else if (loading)
    content = (
      <div className="app-loading">
        <img src="/assets/icono.png" alt="Dilo Shop" />
        <h2>Preparando tus favoritos…</h2>
      </div>
    );
  else if (error)
    content = (
      <div className="app-loading">
        <AlertCircle />
        <h2>No pudimos cargar la tienda</h2>
        <p>{error}</p>
        <button className="btn" onClick={refresh}>
          Volver a intentar
        </button>
        <a href="/config">Ir a administración</a>
      </div>
    );
  else if (route.startsWith("#order/") || route.startsWith("#review/"))
    content = <Tracking key={route} {...props} />;
  else if (admin && user) content = <Admin {...props} />;
  else if (!data?.store)
    content = (
      <div className="app-loading">
        <h1>Dilo Shop</h1>
        <p>La tienda está en preparación.</p>
        <a className="btn" href="/config">
          Configurar mi tienda
        </a>
      </div>
    );
  else content = <Storefront {...props} />;
  return (
    <>
      {content}
      {toast && (
        <div className={`toast ${toast.type}`} role="status">
          <span>{toast.text}</span>
          <button aria-label="Cerrar aviso" onClick={() => setToast(null)}>
            <X size={17} />
          </button>
        </div>
      )}
    </>
  );
}
class ErrorBoundary extends React.Component {
  state = { error: null };
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    return this.state.error ? (
      <div className="app-loading">
        <h1>Algo no salió bien</h1>
        <p>{this.state.error.message}</p>
        <button className="btn" onClick={() => location.reload()}>
          Recargar
        </button>
      </div>
    ) : (
      this.props.children
    );
  }
}
createRoot(document.getElementById("root")).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>,
);
